import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { nanoid } from 'nanoid';
import { api } from '../api/client';
import { queryClient } from '../api/queryClient';
import { useAppStore } from '../store';
import type { Task, Priority, TaskStatus } from '../types';

function invalidateTaskQueries(opts: {
  spaceId?: string;
  parentId?: string | null;
  taskId?: string;
}) {
  const { spaceId, parentId, taskId } = opts;
  const resolvedSpaceId = spaceId || useAppStore.getState().currentSpaceId;
  queryClient.invalidateQueries({ queryKey: ['tasks', resolvedSpaceId, 'track'] });
  queryClient.invalidateQueries({ queryKey: ['tasks', resolvedSpaceId, 'backlog'] });
  if (taskId) {
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });
  }
  if (parentId) {
    queryClient.invalidateQueries({ queryKey: ['subtasks', parentId] });
    queryClient.invalidateQueries({ queryKey: ['subtasks', 'done-count', parentId] });
  }
}

export function useTasksByStatus(status: TaskStatus) {
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const { data } = useQuery({
    queryKey: ['tasks', spaceId, status],
    queryFn: () => api.tasks.list({ spaceId: spaceId!, status }),
    enabled: !!spaceId,
  });
  return (data ?? []) as Task[];
}

export function useTrackTasks(date?: string) {
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const trackDate = date ?? selectedDate;
  const { data } = useQuery({
    queryKey: ['tasks', spaceId, 'track', trackDate],
    queryFn: () => api.tasks.list({ spaceId: spaceId!, status: 'track', trackDate }),
    enabled: !!spaceId,
  });
  return (data ?? []) as Task[];
}

export function useFutureAwareTrackTasks(date: string) {
  const spaceId = useAppStore((s) => s.currentSpaceId);

  // All track tasks for this space (no date filter) - regular tasks always visible
  const { data: allTrackTasks } = useQuery({
    queryKey: ['tasks', spaceId, 'track'],
    queryFn: () => api.tasks.list({ spaceId: spaceId!, status: 'track' }),
    enabled: !!spaceId,
  });

  return useMemo(() => {
    const all = (allTrackTasks ?? []) as Task[];

    // Regular tasks (no templateId): always show
    const regular = all.filter((t) => !t.templateId);

    // Recurring occurrences: filter by date visibility
    const recurring = all.filter((t) => t.templateId);

    // For each template, find the occurrence that should be visible on this date:
    // the one with the latest trackDate <= date
    const latestByTemplate = new Map<string, Task>();
    for (const t of recurring) {
      if (t.trackDate && t.trackDate <= date) {
        const existing = latestByTemplate.get(t.templateId!);
        if (!existing || t.trackDate > existing.trackDate!) {
          latestByTemplate.set(t.templateId!, t);
        }
      }
    }

    return [...regular, ...latestByTemplate.values()];
  }, [allTrackTasks, date]);
}

export function useSubtasks(parentId: string) {
  const { data } = useQuery({
    queryKey: ['subtasks', parentId],
    queryFn: () => api.tasks.getSubtasks(parentId),
  });
  return (data ?? []) as Task[];
}

export function useTaskById(id: string | null) {
  const { data } = useQuery({
    queryKey: ['task', id],
    queryFn: () => id ? api.tasks.getById(id) : null,
    enabled: !!id,
  });
  return data as Task | undefined;
}

export function useDoneSubtaskCount(parentId: string) {
  const { data } = useQuery({
    queryKey: ['subtasks', 'done-count', parentId],
    queryFn: () => api.tasks.getDoneSubtaskCount(parentId),
  });
  return data ?? 0;
}

export function useAllTasks() {
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const { data } = useQuery({
    queryKey: ['tasks', spaceId, 'all'],
    queryFn: () => api.tasks.list({ spaceId: spaceId! }),
    enabled: !!spaceId,
  });
  return (data ?? []) as Task[];
}

export async function createTask(
  title: string,
  spaceId: string,
  priority: Priority = 'p2',
  status: TaskStatus = 'backlog',
  opts?: Partial<Task>
): Promise<Task> {
  const now = new Date().toISOString();
  const task = {
    id: nanoid(),
    title,
    spaceId,
    priority,
    status,
    itemType: 'task',
    trackProgress: 0,
    createdAt: now,
    updatedAt: now,
    ...opts,
  };
  await api.tasks.create(task);
  invalidateTaskQueries({ spaceId, parentId: opts?.parentId });
  return task as Task;
}

export async function updateTask(id: string, changes: Partial<Task>) {
  await api.tasks.update(id, changes);
  invalidateTaskQueries({ taskId: id, parentId: changes.parentId });
}

export async function moveToTrack(id: string, trackDate: string) {
  await api.tasks.moveToTrack(id, trackDate);
  invalidateTaskQueries({});
}

export async function deleteTask(id: string, opts?: { parentId?: string | null }) {
  await api.tasks.delete(id);
  invalidateTaskQueries({ taskId: id, parentId: opts?.parentId });
  queryClient.invalidateQueries({ queryKey: ['timeline-blocks'] });
}
